import React, { useState } from 'react';
import swal from 'sweetalert';
import { API_URL } from '../../constant/API_Settings';
import {
  countWacPreviewChanges,
  wacStepDetailLabel,
  wacStepProductLabel,
  wacStepTankLabel,
  wacTypeLabel,
} from '../../utils/dailyTransactionWacPreview';

const WAC_REQUEST_TIMEOUT_MS = 180000;

function formatNum(val, decimals = 2) {
  if (val === null || val === undefined || val === '') {
    return '—';
  }
  const num = Number(val);
  if (Number.isNaN(num)) {
    return '—';
  }
  return num.toFixed(decimals);
}

export default function DailyTransactionWacPanel({
  fromDate,
  toDate,
  disabled,
  onApplied,
  buttonClassName = 'btn btn-warning w-auto ms-2',
}) {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [previewSummary, setPreviewSummary] = useState('');
  const [previewSteps, setPreviewSteps] = useState([]);
  const [previewAlert, setPreviewAlert] = useState('');
  const [salesOk, setSalesOk] = useState(false);
  const [purchaseOk, setPurchaseOk] = useState(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
    Accept: 'application/json',
  });

  const postWac = async (endpoint) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), WAC_REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(API_URL + endpoint, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          from_date: fromDate,
          to_date: toDate,
        }),
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || data.status === false) {
        throw new Error(data.message || 'WAC request failed.');
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('WAC recalculation timed out after 3 minutes. Try a shorter date range.');
      }
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  };

  const buildPreviewSummary = (data, purchaseChanges, salesChanges) => {
    const summary = data.summary || {};

    let html = '<ul class="mb-0">';
    if (summary.request_from_date || summary.from_date) {
      html += `<li><strong>Selected from date:</strong> ${summary.request_from_date || summary.from_date}</li>`;
    }
    if (summary.anchor_purchase_date || summary.anchor_date) {
      html += `<li><strong>Anchor purchase date:</strong> ${summary.anchor_purchase_date || summary.anchor_date}`;
      if (summary.anchor_unload_id) {
        html += ` (unload #${summary.anchor_unload_id})`;
      }
      html += '</li>';
    }
    if (summary.apply_sales_through || (summary.range_start && summary.range_end)) {
      html += `<li><strong>Recalc range:</strong> ${
        summary.sales_apply_from || summary.range_start || summary.anchor_purchase_date || ''
      } to ${summary.apply_sales_through || summary.range_end || ''}</li>`;
    }
    html += `<li><strong>Purchase rows to change:</strong> ${purchaseChanges}</li>`;
    html += `<li><strong>Sales rows to change:</strong> ${salesChanges}</li>`;
    if (summary.formula) {
      html += `<li><strong>Formula:</strong> ${summary.formula}</li>`;
    }
    html += '<li><strong>Note:</strong> WAC preview lists only sale/recoup/purchase rows where ledger COGS or purchase price differs from replayed WAC — not every fuel sale line on the daily sheet.</li>';
    if (purchaseChanges > 0 && salesChanges > 0) {
      html += '<li><strong>Apply order:</strong> Purchase WAC updates tank prices; sales COGS uses those prices. Apply both (use <em>Apply all</em>) for a complete fix.</li>';
    }
    html += '</ul>';
    return html;
  };

  const buildPreviewAlert = (data, purchaseChanges = 0, salesChanges = 0) => {
    const parts = [data.message || 'Preview loaded. Review changes before applying.'];
    const stats = data.stats || {};
    if (stats.preview_steps_truncated && stats.preview_changes_date_through) {
      parts.push(
        `All ${stats.preview_steps_total ?? stats.rows_with_changes ?? ''} correction(s) span through ${stats.preview_changes_date_through}; the table shows the newest ${stats.preview_steps_shown ?? 500} rows.`
      );
    }
    if (purchaseChanges > 0 && salesChanges > 0) {
      parts.push('Use Apply all corrections (recommended) or apply purchase first, then sales. Preview again after apply — it should show no rows when everything is fixed.');
    } else if (purchaseChanges === 0 && salesChanges === 0) {
      parts.push('No corrections needed for this range — ledger already matches replayed WAC.');
    }
    return parts.join(' ');
  };

  const loadPreviewData = async () => {
    const data = await postWac('/daily_transaction_wac_preview');
    const { purchase: purchaseChanges, sales: salesChanges } = countWacPreviewChanges(
      data.stats || {},
      data.steps || []
    );

    setPreviewSummary(buildPreviewSummary(data, purchaseChanges, salesChanges));
    setPreviewSteps(data.steps || []);
    setPreviewAlert(buildPreviewAlert(data, purchaseChanges, salesChanges));
    setSalesOk(data.status === true && salesChanges > 0);
    setPurchaseOk(data.status === true && purchaseChanges > 0);

    return { data, purchaseChanges, salesChanges };
  };

  const handlePreview = async () => {
    if (!fromDate || !toDate) {
      swal('Please select start and end dates first.', '', 'warning');
      return;
    }
    setLoading(true);
    setLoadingMessage('Loading WAC preview…');
    try {
      await loadPreviewData();
      setShowModal(true);
    } catch (error) {
      swal('WAC Preview', error.message || 'Unable to load preview.', 'error');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const refreshPreviewAfterApply = async (applyMessage) => {
    setLoadingMessage('Refreshing preview…');
    const { purchaseChanges, salesChanges } = await loadPreviewData();
    if (purchaseChanges === 0 && salesChanges === 0) {
      swal('WAC Apply', `${applyMessage}\n\nPreview is clear — no remaining corrections for this range.`, 'success');
    } else {
      swal(
        'WAC Apply',
        `${applyMessage}\n\nPreview still shows ${purchaseChanges} purchase and ${salesChanges} sales row(s) to fix. Apply remaining corrections or review the table.`,
        'warning'
      );
    }
    if (typeof onApplied === 'function') {
      onApplied();
    }
  };

  const handleApply = async (mode) => {
    const endpoint = mode === 'sales'
      ? '/daily_transaction_wac_apply_sales'
      : mode === 'all'
        ? '/daily_transaction_wac_apply'
        : '/daily_transaction_wac_apply_purchase';
    const label = mode === 'sales'
      ? 'sales COGS'
      : mode === 'all'
        ? 'purchase and sales corrections'
        : 'purchase prices';

    const confirmed = await swal({
      title: 'Apply WAC corrections?',
      text: `Apply corrected ${label} for the selected date range?`,
      icon: 'warning',
      buttons: true,
      dangerMode: mode !== 'purchase',
    });
    if (!confirmed) {
      return;
    }

    setLoading(true);
    setLoadingMessage(
      mode === 'all'
        ? 'Applying purchase and sales corrections…'
        : mode === 'sales'
          ? 'Applying sales corrections…'
          : 'Applying purchase corrections…'
    );
    try {
      const data = await postWac(endpoint);
      await refreshPreviewAfterApply(data.message || 'Corrections applied.');
    } catch (error) {
      swal('WAC Apply', error.message || 'Apply failed.', 'error');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const applyAllOk = purchaseOk || salesOk;

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={handlePreview}
        disabled={disabled || loading}
        title="Recalculate weighted average purchase price"
      >
        Recalculate WAC
      </button>

      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.45)', zIndex: 2000 }}>
          <div className="bg-white rounded p-4 text-center shadow">
            <div className="spinner-border text-primary mb-3" role="status" />
            <div>{loadingMessage || 'Processing…'}</div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Weighted average purchase price — preview</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                {previewAlert && <div className="alert alert-info">{previewAlert}</div>}
                {previewSummary && <div className="mb-3 small" dangerouslySetInnerHTML={{ __html: previewSummary }} />}
                <div className="table-responsive">
                  <table className="table table-bordered table-sm">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Date</th>
                        <th>Product Name</th>
                        <th>Detail</th>
                        <th>Tank</th>
                        <th className="text-end">Old PP</th>
                        <th className="text-end">New PP</th>
                        <th className="text-end">Old COGS</th>
                        <th className="text-end">New COGS</th>
                        <th>Calculation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(previewSteps || []).map((step, index) => (
                        <tr key={index} className={step.will_change ? 'table-warning' : ''}>
                          <td>{wacTypeLabel(step.type)}</td>
                          <td>{step.date || step.op_date || '—'}</td>
                          <td>{wacStepProductLabel(step)}</td>
                          <td>{wacStepDetailLabel(step)}</td>
                          <td>{wacStepTankLabel(step)}</td>
                          <td className="text-end">{formatNum(step.old_purchase_price, 2)}</td>
                          <td className="text-end">{formatNum(step.new_purchase_price, 2)}</td>
                          <td className="text-end">{formatNum(step.old_cogs, 2)}</td>
                          <td className="text-end">{formatNum(step.new_cogs, 2)}</td>
                          <td style={{ fontSize: '12px', maxWidth: '240px' }}>{step.calculation || ''}</td>
                        </tr>
                      ))}
                      {previewSteps.length === 0 && (
                        <tr><td colSpan="10" className="text-center text-muted">No steps in preview.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button type="button" className="btn btn-warning" disabled={!applyAllOk || loading} onClick={() => handleApply('all')}>
                  Apply all corrections
                </button>
                <button type="button" className="btn btn-primary" disabled={!purchaseOk || loading} onClick={() => handleApply('purchase')}>
                  Apply purchase only
                </button>
                <button type="button" className="btn btn-success" disabled={!salesOk || loading} onClick={() => handleApply('sales')}>
                  Apply sales only
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
